import { useCallback, useMemo, useState } from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Snackbar from '@mui/material/Snackbar';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { api, ApiError } from '@/lib/api/client';
import type { AnswerInput, OrganisationType, ScoringResult } from '@/lib/api/types';
import { answerKey } from '@/lib/answerKey';
import { t } from '@/lib/strings';
import { ErrorState, LoadingState } from '@/components/LoadingState';
import { AnswerOptionList } from '@/features/questionnaire/AnswerOptionList';
import { JurisdictionGrid } from '@/features/questionnaire/JurisdictionGrid';
import { LiveScoreHeader } from '@/features/questionnaire/LiveScoreHeader';
import { NotesPanel } from '@/features/questionnaire/NotesPanel';
import { QuestionCard } from '@/features/questionnaire/QuestionCard';
import {
  answersGiven,
  buildFlow,
  isStepAnswered,
  totalAnswers,
} from '@/features/questionnaire/questionFlow';

interface SelfServiceNavState {
  organisationType?: OrganisationType;
  scope?: 'light' | 'detaljeret';
  systemName?: string;
}

/**
 * The self-service twin of QuestionnairePage: same flow logic and the same shared display
 * components, and the internal-note field hidden — there is no rep here to leave themselves a
 * note.
 */
export function SelfServiceQuestionnairePage() {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const navState = location.state as SelfServiceNavState | null;
  const organisationType = navState?.organisationType ?? 'private';
  const scope = navState?.scope ?? 'detaljeret';
  const systemName = navState?.systemName;

  const [answers, setAnswers] = useState<Record<string, AnswerInput>>({});
  const [stepIndex, setStepIndex] = useState(0);
  const [result, setResult] = useState<ScoringResult | undefined>();
  const [failure, setFailure] = useState<string | null>(null);

  const definitionQuery = useQuery({
    queryKey: ['questionnaire', organisationType, scope],
    queryFn: () => api.questionnaire(organisationType, scope),
  });

  const save = useMutation({
    mutationFn: (batch: AnswerInput[]) => api.selfServiceSaveAnswers(id, batch),
    onSuccess: (scoring) => setResult(scoring),
    onError: (error) =>
      setFailure(error instanceof ApiError ? error.userMessage : t('fejl.generel')),
  });

  const definition = definitionQuery.data;
  const flow = useMemo(() => (definition ? buildFlow(definition) : []), [definition]);
  const step = flow[stepIndex];

  const record = useCallback(
    (input: AnswerInput) => {
      const key = answerKey(input.questionId, input.layer);
      setAnswers((current) => ({ ...current, [key]: input }));
      save.mutate([input]);
    },
    [save],
  );

  if (definitionQuery.isPending) {
    return <LoadingState />;
  }

  if (definitionQuery.isError || !definition) {
    return <ErrorState onRetry={() => void definitionQuery.refetch()} />;
  }

  if (!step) {
    return <ErrorState message={t('fejl.generel')} />;
  }

  const total = totalAnswers(flow, definition);
  const given = answersGiven(flow, definition, answers);
  const stepDone = isStepAnswered(step, definition, answers);
  const isLast = stepIndex === flow.length - 1;
  const current = answers[answerKey(step.question.id)];

  return (
    <Box sx={{ maxWidth: 900, mx: 'auto' }}>
      <LiveScoreHeader systemName={systemName} result={result} answered={given} total={total} />

      <QuestionCard
        question={step.question}
        sectionTitle={step.sectionTitle}
        footer={
          <NotesPanel
            rationale={current?.rationale ?? ''}
            internalNote=""
            showInternalNote={false}
            onRationaleChange={(value) =>
              current && record({ ...current, rationale: value || null })
            }
            onInternalNoteChange={() => {}}
          />
        }
      >
        {step.perLayer ? (
          <JurisdictionGrid
            layers={definition.layers}
            jurisdictions={definition.jurisdictions}
            values={Object.fromEntries(
              definition.layers.map((layer) => [
                layer.id,
                answers[answerKey(step.question.id, layer.id)]?.code ?? undefined,
              ]),
            )}
            onChange={(layer, value) =>
              record({ questionId: step.question.id, layer, code: value, notMapped: false })
            }
          />
        ) : (
          <AnswerOptionList
            name={step.question.text}
            options={step.question.options}
            selected={current?.value ?? null}
            onSelect={(value) =>
              record({
                questionId: step.question.id,
                value: typeof value === 'number' ? value : Number(value),
                notMapped: false,
                rationale: current?.rationale ?? null,
                internalNote: null,
              })
            }
          />
        )}

        {step.question.allowNotMapped && !step.perLayer && (
          <Button
            variant="outlined"
            size="small"
            sx={{ mt: 2 }}
            aria-pressed={current?.notMapped === true}
            onClick={() =>
              record({
                questionId: step.question.id,
                notMapped: true,
                rationale: current?.rationale ?? null,
                internalNote: null,
              })
            }
          >
            {t('skema.ikkeKortlagt')}
          </Button>
        )}
      </QuestionCard>

      <Box sx={{ display: 'flex', gap: 2, mt: 3, flexWrap: 'wrap' }}>
        <Button
          variant="outlined"
          size="large"
          disabled={stepIndex === 0}
          onClick={() => setStepIndex((index) => Math.max(0, index - 1))}
        >
          {t('handling.tilbage')}
        </Button>

        <Box sx={{ flexGrow: 1 }} />

        {isLast ? (
          <Button
            variant="contained"
            size="large"
            disabled={!result?.isComplete}
            onClick={() => navigate(`/selvbetjening/${id}/resultat`)}
          >
            {t('handling.afslut')}
          </Button>
        ) : (
          <Button
            variant="contained"
            size="large"
            onClick={() => setStepIndex((index) => Math.min(flow.length - 1, index + 1))}
          >
            {stepDone ? t('handling.videre') : t('handling.spring')}
          </Button>
        )}
      </Box>

      <Snackbar
        open={failure !== null}
        autoHideDuration={8000}
        onClose={() => setFailure(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="warning" onClose={() => setFailure(null)}>
          {failure}
        </Alert>
      </Snackbar>
    </Box>
  );
}
